import React, { useState, useEffect } from 'react';
import { Box, VStack, Heading, Text, Button, Spinner, useToast, HStack, Table, Thead, Tbody, Tr, Th, Td, Badge } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../services/supabaseClient';
import { useApp } from '../../context/AppContext';

const MotionBox = motion(Box);

const AgendaPage = () => {
  const [atendimentos, setAtendimentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const toast = useToast();
  const { isAdmin } = useApp();

  // Verificar se o usuário é administrador
  useEffect(() => {
    if (!isAdmin) {
      navigate('/menu');
      toast({
        title: 'Acesso restrito',
        description: 'Você não tem permissão para acessar esta página.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  }, [isAdmin, navigate, toast]);

  // Buscar atendimentos do dia
  useEffect(() => {
    const fetchAtendimentos = async () => {
      setLoading(true);
      
      // Obter data de hoje (início e fim do dia)
      const today = new Date();
      const startOfDay = new Date(today.setHours(0, 0, 0, 0)).toISOString();
      const endOfDay = new Date(today.setHours(23, 59, 59, 999)).toISOString();
      
      const { data, error } = await supabase
        .from('atendimentos')
        .select('*, atendimentos_servicos(servico_id, servicos(nome))')
        .gte('iniciado_em', startOfDay)
        .lte('iniciado_em', endOfDay)
        .order('iniciado_em', { ascending: false });

      if (error) {
        console.error('Erro ao buscar atendimentos:', error);
        toast({
          title: 'Erro ao carregar agenda',
          description: error.message,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      } else {
        setAtendimentos(data || []);
      }
      setLoading(false);
    };

    fetchAtendimentos();
    
    // Atualizar a cada 5 minutos
    const interval = setInterval(fetchAtendimentos, 300000);
    return () => clearInterval(interval);
  }, [toast]);

  const handleBack = () => {
    navigate('/menu');
  };
  
  // Formatar hora
  const formatTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };
  
  // Calcular duração do atendimento
  const calcularDuracao = (inicio, fim) => {
    if (!inicio || !fim) return '-';
    
    const inicioDate = new Date(inicio);
    const fimDate = new Date(fim);
    const diffMs = fimDate - inicioDate;
    
    const minutes = Math.floor(diffMs / 60000);
    if (minutes < 60) {
      return `${minutes} min`;
    } else {
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      return `${hours}h ${remainingMinutes}min`;
    }
  };
  
  // Obter status com cor
  const getStatusBadge = (status) => {
    let color = 'gray';
    let label = status;
    
    switch(status) {
      case 'em_andamento':
        color = 'yellow';
        label = 'Em Andamento';
        break;
      case 'concluido':
        color = 'green';
        label = 'Concluído';
        break;
      case 'cancelado':
        color = 'red';
        label = 'Cancelado';
        break;
      case 'mobile_pending':
        color = 'blue';
        label = 'Mobile Pendente';
        break;
      default:
        color = 'gray';
        label = status || 'Desconhecido';
    }
    
    return <Badge colorScheme={color}>{label}</Badge>;
  };

  return (
    <MotionBox
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      minHeight="100vh"
      width="100%"
      bg="black"
      p={6}
    >
      <VStack spacing={6} align="stretch">
        <HStack justifyContent="space-between" alignItems="center">
          <Heading color="yellow.400" size="xl">Agenda do Dia</Heading>
          <Button colorScheme="gray" variant="outline" onClick={handleBack}>
            Voltar ao Menu
          </Button>
        </HStack>

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minH="300px">
            <Spinner size="xl" color="yellow.400" thickness="4px" />
          </Box>
        ) : (
          <>
            <Text color="gray.300" fontSize="lg" mb={4}>
              Total de atendimentos hoje: {atendimentos.length}
            </Text>
            
            <Box overflowX="auto">
              <Table variant="simple" colorScheme="whiteAlpha">
                <Thead>
                  <Tr>
                    <Th color="yellow.400">Horário</Th>
                    <Th color="yellow.400">Cliente</Th>
                    <Th color="yellow.400">Barbeiro</Th>
                    <Th color="yellow.400">Serviços</Th>
                    <Th color="yellow.400">Duração</Th>
                    <Th color="yellow.400">Status</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {atendimentos.map((atendimento) => (
                    <Tr key={atendimento.id}>
                      <Td color="white">{formatTime(atendimento.iniciado_em)}</Td>
                      <Td color="white">
                        {atendimento.nome_cliente}
                        {atendimento.mensalista && 
                          <Badge ml={2} colorScheme="yellow">Mensalista</Badge>
                        }
                      </Td>
                      <Td color="white">{atendimento.barbeiro || '-'}</Td>
                      <Td color="white">
                        {atendimento.atendimentos_servicos?.length > 0 
                          ? atendimento.atendimentos_servicos.map(s => s.servicos?.nome).join(', ')
                          : '-'
                        }
                      </Td>
                      <Td color="white">{calcularDuracao(atendimento.iniciado_em, atendimento.finalizado_em)}</Td>
                      <Td>{getStatusBadge(atendimento.status)}</Td>
                    </Tr>
                  ))}
                  
                  {atendimentos.length === 0 && (
                    <Tr>
                      <Td colSpan={6} textAlign="center" py={8} color="gray.500">
                        Nenhum atendimento registrado hoje
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </Box>
          </>
        )}
      </VStack>
    </MotionBox>
  );
};

export default AgendaPage;
